import { CategoryPreviewCard } from "../CategoryPreviewCard/CategoryPreviewCard";
import { DashboardViewModel, dashboard } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import {
	ReactElement,
	useEffect,
	useState,
	useCallback
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { BusyIndicator, NoResults } from "client/Components";
import { ListLayout } from "../../ListLayout/ListLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubEvent, subscribe } from "../../events";
import { unsubscribe } from "pubsub-js";
import { useForceRerender } from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type DashboardProps = {
	onCategoryClick: (categoryId: number) => void;
	onNavigateToItem: (id: number) => void;
	timezone: TimeZone;
};

export const Dashboard = (props: DashboardProps): ReactElement => {
	const {
		onCategoryClick,
		onNavigateToItem,
		timezone
	} = props;

	const [dashboardModel, setDashboardModel] = useState<DashboardViewModel | null>(null);
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [reloadData, dataVersion] = useForceRerender();

	const fetchDashboard = useCallback(
		async (signal: AbortSignal) => {
			try {
				setIsLoading(true);
				setHasError(false);

				const response: DashboardViewModel = await dashboard(signal);
				setDashboardModel(response);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setHasError(true);
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(dataVersion);
			const cancellation = new AbortController();
			fetchDashboard(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			dataVersion,
			fetchDashboard
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			const token: PubSubSubscriptionToken = subscribe(
				HubEvent.HierarchyChanged,
				(): void => {
					reloadData();
				}
			);

			return (): void => unsubscribe(token);
		},
		[reloadData]
	);

	if (isLoading) {
		return <BusyIndicator />;
	}

	if (hasError) {
		return (
			<NoResults
				icon="gear"
				title={localize("_NotAbleToRetrieveKnowledgeArticles")}
				message={localize("_WeWereUnableToRetrieveYourKnowledgeArticles")}
				onRetry={reloadData}
				height="fill"
			/>
		);
	}

	if (!dashboardModel || dashboardModel.categories.length === 0) {
		return (
			<NoResults
				icon="book"
				title={localize("_NoKnowledgeArticlesYet")}
				message={localize("_ThereAreNoPublishedItemsYet")}
				height="fill"
			/>
		);
	}

	return (
		<ListLayout
			trackingId="knowledge-items-category-view"
			renderAfterItems={<ListLayout.MobilePlusMenuSpacing />}
		>
			{dashboardModel.categories.map((category) => (
				<ListLayout.Item
					key={category.id}
				>
					<CategoryPreviewCard
						category={category}
						onCategoryClick={onCategoryClick}
						onNavigateToItem={onNavigateToItem}
						timezone={timezone}
					/>
				</ListLayout.Item>
			))}
		</ListLayout>
	);
};

Dashboard.displayName = "Dashboard";
